'use client';

import { useEffect, useRef, useState } from 'react';
import { X, Check, Phone, Clock, AlertTriangle, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { cn } from '@/lib/utils';

const ProblemCondensed = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const painPoints = [
    { icon: Phone, text: 'Endless vendor phone calls and email chains' },
    { icon: Clock, text: 'No visibility into what\'s actually happening' },
    { icon: AlertTriangle, text: 'Scattered tools, scattered data' },
  ];

  const solutions = [
    { text: 'One dashboard for all properties and vendors' },
    { text: 'Real-time visibility for you AND your owners' },
    { text: 'Everything in one place — finally' },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-14 sm:py-20 lg:py-24 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-500/[0.02] to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2
            className={cn(
              "font-playfair text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-secondary-100 mb-4",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
          >
            You Manage Properties.
            <span className="text-gray-500 dark:text-secondary-400"> Not Chaos.</span>
          </h2>
        </div>

        {/* Before / After - Compact */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-10">
          {/* Before */}
          <div
            className={cn(
              "relative p-5 rounded-2xl",
              "bg-gradient-to-br from-danger-500/5 to-transparent",
              "border border-danger-500/10",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            )}
          >
            <div className="absolute -top-3 left-5">
              <span className="px-3 py-1 bg-[#FAFBFC] dark:bg-[#0d0d0d] text-danger-400 text-xs font-semibold uppercase tracking-wide">
                Without Axis Datum
              </span>
            </div>
            <div className="space-y-2.5 mt-3">
              {painPoints.map((point, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-2 rounded-lg bg-danger-500/5 border border-danger-500/10"
                >
                  <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-danger-500/10 flex items-center justify-center">
                    <point.icon className="w-3.5 h-3.5 text-danger-400" />
                  </div>
                  <span className="text-gray-600 dark:text-secondary-300 text-sm flex-1">{point.text}</span>
                  <X className="w-3.5 h-3.5 text-danger-500 flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>

          {/* After */}
          <div
            className={cn(
              "relative p-5 rounded-2xl",
              "bg-gradient-to-br from-success-500/5 to-transparent",
              "border border-success-500/10",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            )}
          >
            <div className="absolute -top-3 left-5">
              <span className="px-3 py-1 bg-[#FAFBFC] dark:bg-[#0d0d0d] text-success-400 text-xs font-semibold uppercase tracking-wide">
                With Axis Datum
              </span>
            </div>
            <div className="space-y-2.5 mt-3">
              {solutions.map((point, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-2 rounded-lg bg-success-500/5 border border-success-500/10"
                >
                  <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-success-500/10 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-success-400" />
                  </div>
                  <span className="text-gray-700 dark:text-secondary-200 text-sm">{point.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quote + Link */}
        <div
          className={cn(
            "text-center",
            "transition-all duration-700",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          )}
        >
          <p className="text-lg font-playfair text-gray-600 dark:text-secondary-300 mb-6">
            "Stop reacting to chaos.
            <span className="text-primary-600 dark:text-primary-400"> Start managing with clarity."</span>
          </p>
          <Link
            href="/how-it-works"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors"
          >
            See how it works
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export { ProblemCondensed };
